<template>
  <div class="m-tiles-wrapper">
    <ContextMenu ref="contextMenu" :model="contextMenuModel" @hide="onTilesContextMenuHide" />
    <div
      class="m-tiles-items"
      :style="itemsStyle"
      @contextmenu.capture.prevent.stop="handleNativeTilesContextMenu"
    >
      <SwingTileRow
        v-for="(row, rowIndex) in rowsResolved"
        :key="swingTileRowKey(row, rowIndex)"
        :row-data="rowDataProp(row)"
        :panel-data="panelTemplate"
        :page-id="pageId"
        :row-index="rowIndex"
        :table-field-name="tableFieldName"
        :tiles-element-id="tilesElementId"
        :parent-panel-id="parentPanelId"
        :row-visible="isRowVisible(row, rowIndex)"
        :commit-row-field="(fn, v) => swingCommitRowField(rowIndex, fn, v)"
        :get-row-field-value="(fn) => swingGetRowFieldValue(rowIndex, fn)"
        :row-action-visibility-map="rowActionVisibility"
        :after-row-mount="() => queueInlineAfterLoad(rowIndex)"
        @contextmenu="forwardContextMenu"
        @click="forwardRowClick"
      />
    </div>
    <div v-if="hint" class="m-tiles-hint">{{ hint }}</div>
  </div>
</template>

<script setup lang="ts">
import UniData from '@/common/classes/UniData'
import { FormProviderKey, SwingTilesActionVisibilityMapKey, SwingTilesHasInlineAfterLoadKey, SwingTilesInlineVisibilityTargetsKey } from '@/components/platform/injection-keys'
import { extractUniDataRowColumnValue } from '@/components/ui/table/formatters'
import { usePageData } from '@/common/composables/usePageData'
import { useTableContextMenu } from '@/common/composables/useTableContextMenu'
import {
  buildTilesInlineElementVisibilityName,
  buildTilesRowActionVisibilityBucketKey,
  readTilesRowActionVisibilityBucket,
  resolveTilesInlineAfterLoadRowIndex,
  resolveTilesRowWrapVisibility,
  setTilesRowActionVisibility,
  writeTilesRowActionVisibilityBucket,
  type TilesRowActionVisibilityMap,
  type TilesRowActionVisibilityStore,
} from '@/common/utils/tilesSetInlineVisibility'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import { resolveTilesRowsFromSources } from '@/common/utils/resolveTilesRowsFromSources'
import { setType } from '@/common/utils/typedValueSerializer'
import {
  applyTilesRowVisibilityStates,
  buildTilesInlineAfterLoadCurrentDataFields,
  buildTilesInlineAfterLoadRowSignature,
  buildTilesInlineAfterLoadRowsSignature,
  buildTilesInlineRowSelectedData,
  collectChangedInlineAfterLoadRowIndexes,
  listTilesRowIndexesForReloadInlineAfterLoad,
  ensureTilesInlineVisibilityForInlineAfterLoad,
  isTilesElementWithInlineVisibilityRegistered,
  parseTilesInlineVisibilityTargets,
  remapExchangeStatesForTilesRow,
  resolveTilesInlineAfterLoadScript,
  shouldRunTilesInlineAfterLoadOnClient,
} from '@/common/utils/tilesInlineAfterLoad'
import {
  buildTilesInlineGetDataFields,
  isTilesInlineLayoutAction,
} from '@/common/utils/tilesInlineGetData'
import { normalizeExchangeStates } from '@/common/utils/normalizeExchangeStates'
import { createSafeClientData } from '@/services/core/core.service'
import { isLazyFormPage } from '@/common/utils/lazyFormPage'
import { scriptService } from '@/services/script/script.service'
import { ScriptLevel, ScriptName } from '@/services/websocket/types'
import SwingTileRow from '@/components/ui/SwingTileRow.vue'
import ContextMenu from 'primevue/contextmenu'
import { computed, inject, nextTick, provide, ref, watch } from 'vue'

interface Props {
  modelValue?: unknown
  tilePanelId?: number
  tilesElementId?: number
  parentPanelId?: string | number
  pageId?: string | number
  formId?: string | number
  tableFieldName?: string
  /** Текст скрипта или узел ScriptData (`_text` + `_attributes.web_client_exec`). */
  inlineAfterLoad?: string | Record<string, unknown> | null
  hint?: string
  gapPx?: number
  expandByDefault?: boolean
  contextMenuItems?: any[]
  /**
   * Async-хук до show контекстного меню. Vue emit не ждёт parent listener,
   * поэтому ПКМ плитки не должен собирать модель из props до onInlineMenuShow.
   */
  beforeContextMenuShow?: (event: {
    selectedIdx: number
    target: EventTarget | null
    column: string
    data: unknown
    preview?: boolean
  }) => void | any[] | Promise<void | any[]>
}

const props = withDefaults(defineProps<Props>(), {
  tilePanelId: 0,
  tilesElementId: 0,
  parentPanelId: '',
  pageId: '',
  formId: '',
  tableFieldName: '',
  inlineAfterLoad: () => null,
  hint: '',
  gapPx: 5,
  contextMenuItems: () => [],
})

type TilesRowClickPayload = {
  data: unknown
  selectedIdx: number
}

type TilesContextMenuPayload = {
  data: unknown
  selectedIdx: number
  target: EventTarget | null
  originalEvent: MouseEvent
}

const emit = defineEmits<{
  'update:modelValue': [value: { rows: unknown[] }]
  click: [payload: TilesRowClickPayload]
  contextmenu: [payload: TilesContextMenuPayload]
}>()

const contextMenuItemsAfterShow = ref<any[] | null>(null)

const { contextMenu, contextMenuModel, handleContextMenu: handleTilesContextMenu, onContextMenuHide } = useTableContextMenu({
  contextMenuItems: computed(() => props.contextMenuItems || []),
  getContextMenuItems: () => contextMenuItemsAfterShow.value ?? props.contextMenuItems ?? [],
  headers: computed(() => []),
  onEmit: async (event) => {
    if (event.preview !== true) {
      contextMenuItemsAfterShow.value = null
    }
    const payload = {
      selectedIdx: typeof event.selectedIdx === 'number' ? event.selectedIdx : -1,
      target: event.target ?? null,
      column: typeof event.column === 'string' ? event.column : '',
      data: event.data,
      preview: event.preview === true,
    }
    if (typeof props.beforeContextMenuShow === 'function') {
      const result = await props.beforeContextMenuShow(payload)
      if (Array.isArray(result)) {
        if (event.preview !== true) {
          contextMenuItemsAfterShow.value = result
        }
        return result
      }
      return
    }
    if (event.preview === true) return
    emit('contextmenu', {
      data: event.data,
      selectedIdx: payload.selectedIdx,
      target: event.target,
      originalEvent: event.target,
    })
  },
})

function onTilesContextMenuHide(): void {
  contextMenuItemsAfterShow.value = null
  onContextMenuHide()
}

/** Проброс ПКМ со строки SwingTileRow в useTableContextMenu. */
function forwardContextMenu(payload: unknown): void {
  const p = payload as Partial<TilesContextMenuPayload> | null | undefined
  if (!p || p.data === undefined || typeof p.selectedIdx !== 'number') {
    return
  }
  swingSelectedRowIndex.value = p.selectedIdx
  void handleTilesContextMenu(p.originalEvent || p.target, p.data, undefined, { selectedIdx: p.selectedIdx })
}

function forwardRowClick(payload: unknown): void {
  const p = payload as Partial<TilesRowClickPayload> | null | undefined
  if (!p || p.data === undefined || typeof p.selectedIdx !== 'number') {
    return
  }
  swingSelectedRowIndex.value = p.selectedIdx
  emit('click', {
    data: p.data,
    selectedIdx: p.selectedIdx,
  })
}

/** ПКМ по области tiles: резолвит строку по `.swing-tile-row[data-row-index]`. */
function handleNativeTilesContextMenu(e: MouseEvent): void {
  const target = e.target as HTMLElement | null
  const rowEl = target?.closest?.('.swing-tile-row') as HTMLElement | null
  if (!rowEl) return
  const idxRaw = rowEl.getAttribute('data-row-index')
  const selectedIdx = idxRaw != null ? Number.parseInt(idxRaw, 10) : Number.NaN
  if (!Number.isFinite(selectedIdx) || selectedIdx < 0) return
  swingSelectedRowIndex.value = selectedIdx
  const rowData = rowsResolved.value[selectedIdx]
  void handleTilesContextMenu(e, rowData, undefined, { selectedIdx })
}

const formProvider = inject(FormProviderKey, null)
const { pageData, pageFields, pageDataSlot, pageRegistryFormId } = usePageData()

/** Нормализация modelValue / pageField → массив строк UniDataTable. */
function normalizeTilesRows(raw: unknown): unknown[] {
  return extractUniDataTableRows(raw)
}

/** Достаёт `rows` из типизированного поля (разные уровни вложенности value/fields). */
function pickRowsFromTypedField(field: any): unknown[] {
  if (!field || typeof field !== 'object') return []
  const candidates = [
    field?.rows,
    field?.value?.rows,
    field?.value?.value?.rows,
    field?.value?.fields?.rows,
    field?.value?.fields?.fields?.rows,
  ]
  for (const c of candidates) {
    if (Array.isArray(c)) return c
    if (c && typeof c === 'object' && typeof c.length === 'number') {
      try {
        return Array.from(c as ArrayLike<unknown>)
      } catch {
        // ignore
      }
    }
  }
  return []
}

/**
 * Fallback-источники строк из registry getForm / uniData / changed_data,
 * если pageFields ещё пуст (до reload/panel).
 */
function getRowsFromRegistry(fieldName: string): unknown[] {
  if (!formProvider || !props.formId || !props.parentPanelId || !props.tilesElementId) return []
  const slot = formProvider.getForm({
    formId: props.formId,
    panelId: props.parentPanelId,
    elementId: props.tilesElementId,
  })
  const panelData = formProvider.getForm({
    formId: props.formId,
    panelId: props.parentPanelId,
  })
  const formData = formProvider.getForm({
    formId: props.formId,
  })

  const pd = pageData.value as Record<string, any> | undefined
  const slotField = (slot as Record<string, any> | undefined)?.[fieldName]
  const panelField = (panelData as Record<string, any> | undefined)?.[fieldName]
  const formField = (formData as Record<string, any> | undefined)?.[fieldName]

  const fromSlot = (() => {
    const byPath = pickRowsFromTypedField(slotField)
    if (byPath.length > 0) return byPath
    return normalizeTilesRows(slotField)
  })()
  const fromPanel = (() => {
    const byPath = pickRowsFromTypedField(panelField)
    if (byPath.length > 0) return byPath
    return normalizeTilesRows(panelField)
  })()
  const fromForm = (() => {
    const byPath = pickRowsFromTypedField(formField)
    if (byPath.length > 0) return byPath
    return normalizeTilesRows(formField)
  })()
  const fromUniData = normalizeTilesRows(pd?.uniData?.fields?.fields?.[fieldName])
  const fromChanged = normalizeTilesRows(pd?.jsResult?.changed_data?.fields?.fields?.[fieldName])
  const fromChain = normalizeTilesRows(pd?.currentChainData?.fields?.fields?.[fieldName])

  const rows =
    (fromUniData.length > 0 && fromUniData) ||
    (fromChanged.length > 0 && fromChanged) ||
    (fromChain.length > 0 && fromChain) ||
    (fromSlot.length > 0 && fromSlot) ||
    (fromPanel.length > 0 && fromPanel) ||
    (fromForm.length > 0 && fromForm) ||
    []
  return rows
}

/** Берёт строки tiles напрямую из `pageFields[fieldName]` (source of truth после reload/panel). */
function resolveTilesRowsFromPageField(fieldName: string): unknown[] {
  const pageField = (pageFields.value as Record<string, unknown> | undefined)?.[fieldName]
  if (pageField === undefined || pageField === null) return []
  return extractUniDataTableRows(pageField)
}

const rowsResolved = computed(() => {
  const fieldName = String(props.tableFieldName || '').trim()
  const pageFieldsMap = (pageFields.value ?? {}) as Record<string, unknown>
  const pageFieldDefined = Boolean(fieldName && Object.prototype.hasOwnProperty.call(pageFieldsMap, fieldName))
  const fromPageField = fieldName ? resolveTilesRowsFromPageField(fieldName) : []

  // После getPage / reload/panel pageFields — единственный источник (Swing parity).
  if (pageFieldDefined) {
    return fromPageField
  }

  const fromModel = normalizeTilesRows(props.modelValue)
  const fromRegistry = fieldName ? getRowsFromRegistry(fieldName) : []
  const merged = resolveTilesRowsFromSources(fromPageField, fromModel)
  if (merged.length > 0) {
    return merged
  }
  return fromRegistry
})

/** Рекурсивный поиск панели шаблона по id в дереве form (main/panels/…). */
function resolvePanelInTree(node: unknown, wantedId: string): Record<string, unknown> | null {
  if (!node || typeof node !== 'object') return null
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = resolvePanelInTree(item, wantedId)
      if (found) return found
    }
    return null
  }
  const rec = node as Record<string, unknown>
  const pid = rec.panelId ?? rec.id ?? (rec._attributes as any)?.id
  if (pid != null && String(pid) === wantedId && Array.isArray(rec.rows)) {
    return rec
  }
  for (const value of Object.values(rec)) {
    const found = resolvePanelInTree(value, wantedId)
    if (found) return found
  }
  return null
}

/** Шаблон панели для клонов строк (`tilePanelId`). */
const panelTemplate = computed(() => {
  const form = (pageData.value as Record<string, unknown> | undefined)?.form as Record<string, unknown> | undefined
  if (!form || !props.tilePanelId) {
    return null
  }
  const id = String(props.tilePanelId)
  const panels = form.panels as Record<string, unknown> | undefined
  const byMap = panels?.[id]
  if (byMap && typeof byMap === 'object') {
    return byMap as Record<string, unknown>
  }
  return resolvePanelInTree(form, id)
})

const itemsStyle = computed(
  () =>
    ({
      display: 'flex',
      flexDirection: 'column' as const,
      gap: `${props.gapPx}px`,
      width: '100%',
    }),
)

const rowWrapVisible = ref<Record<number, boolean>>({})
/** Row-local override. Store на GlobalProvider переживает remount, бакет — слот формы + Tiles. */
const tilesRowActionVisibilityStore =
  formProvider?.getTilesRowActionVisibilityStore?.() ?? ref<TilesRowActionVisibilityStore>({})
const tilesVisibilityBucketKey = computed(() =>
  buildTilesRowActionVisibilityBucketKey({
    pageDataSlot: pageDataSlot.value,
    formId: props.formId,
    tableFieldName: props.tableFieldName,
    tilesElementId: props.tilesElementId,
  }),
)
const rowActionVisibility = computed<TilesRowActionVisibilityMap>({
  get() {
    return readTilesRowActionVisibilityBucket(
      tilesRowActionVisibilityStore.value,
      tilesVisibilityBucketKey.value,
    )
  },
  set(map) {
    tilesRowActionVisibilityStore.value = writeTilesRowActionVisibilityBucket(
      tilesRowActionVisibilityStore.value,
      tilesVisibilityBucketKey.value,
      map,
    )
  },
})
provide(SwingTilesActionVisibilityMapKey, rowActionVisibility)
const hasInlineAfterLoadScript = computed(
  () => Boolean(resolveTilesInlineAfterLoadScript(props.inlineAfterLoad).text),
)
provide(SwingTilesHasInlineAfterLoadKey, hasInlineAfterLoadScript)
const inlineVisibilityTargets = computed(() =>
  parseTilesInlineVisibilityTargets(resolveTilesInlineAfterLoadScript(props.inlineAfterLoad).text),
)
provide(SwingTilesInlineVisibilityTargetsKey, inlineVisibilityTargets)
const swingCurrentInlineRowIndex = ref<number | null>(null)
/** Текущая строка для SwingTiles.getInlineData (клик / ПКМ), не сбрасывается после inlineAfterLoad. */
const swingSelectedRowIndex = ref<number | null>(null)
const lazyReloadRequested = ref(false)
/** Очередь per-row inlineAfterLoad: общий En и swingCurrentInlineRowIndex не реентерабельны. */
let inlineAfterLoadChain: Promise<void> = Promise.resolve()

/** Поставить inlineAfterLoad строки в очередь (последовательно). */
function queueInlineAfterLoad(rowIndex: number): void {
  inlineAfterLoadChain = inlineAfterLoadChain
    .then(
      () =>
        new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve())
        }),
    )
    .then(() => swingInlineAfterLoad(rowIndex))
    .catch((error: unknown) => {
      console.error('[MTiles] inlineAfterLoad failed', { rowIndex, error })
    })
}

/**
 * Page id для lazy-reload: только страница с lazy=true.
 * Пустые Tiles на обычной странице не подгружают page (иначе reload/element бьёт в уже закрытый core).
 */
function resolveLazyPageId(): number | null {
  const form = (pageData.value as Record<string, any> | undefined)?.form
  const direct = Number(props.pageId)
  if (Number.isFinite(direct) && direct > 0) {
    return isLazyFormPage(form, direct) ? direct : null
  }

  const wantedPanelId = String(props.parentPanelId || '').trim()
  if (!wantedPanelId) return null
  if (!form || typeof form !== 'object') return null

  const collectPages = (): any[] => {
    const pages: any[] = []
    const pushPages = (node: any) => {
      if (!node) return
      if (Array.isArray(node)) {
        node.forEach(pushPages)
        return
      }
      if (node.panelRows) pages.push(node)
      if (node.main) pushPages(node.main)
      if (node.header) pushPages(node.header)
      if (node.footer) pushPages(node.footer)
    }
    pushPages(form.main)
    pushPages(form.header)
    pushPages(form.footer)
    return pages
  }

  const pages = collectPages()
  for (const p of pages) {
    const pid = Number(p?.id)
    if (!Number.isFinite(pid)) continue
    const rows = Array.isArray(p?.panelRows) ? p.panelRows : []
    const hasPanel = rows.some((row: any) =>
      Array.isArray(row)
        ? row.some((cell: any) => String(cell?.panelId ?? cell?.id ?? cell?._attributes?.id ?? '') === wantedPanelId)
        : false,
    )
    if (hasPanel && (p?.lazy === true || p?.lazy === 'true')) return pid
  }
  return null
}

/** Сырая строка UniDataTable как объект для SwingTileRow / action selectedData. */
function rowDataProp(row: unknown): Record<string, unknown> {
  return row && typeof row === 'object' && !Array.isArray(row)
    ? (row as Record<string, unknown>)
    : {}
}

/** Парсинг boolean-флагов из строки/числа UniData (1/0, true/false). */
function toBooleanFlag(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === '1' || v === 'true') return true
    if (v === '0' || v === 'false') return false
  }
  return null
}

/**
 * Видимость строки по expandByDefault: поле строки → props.expandByDefault → true.
 * Swing: скрытая строка не участвует в getData.
 */
function resolveRowExpandByDefault(row: unknown): boolean {
  const raw = extractUniDataRowColumnValue(row, 'expandByDefault')
  const byRow = toBooleanFlag(raw)
  if (byRow !== null) return byRow
  if (props.expandByDefault === false) return false
  return true
}

/**
 * Видна ли обёртка строки: override из setInlineVisibility(boolean) либо expandByDefault.
 */
function isRowVisible(row: unknown, rowIndex: number): boolean {
  const forced = rowWrapVisible.value[rowIndex]
  return resolveTilesRowWrapVisibility(forced, resolveRowExpandByDefault(row))
}

/** Стабильный :key клона (rowIndex + _id документа строки). */
function swingTileRowKey(row: unknown, rowIndex: number): string {
  const r = row as Record<string, unknown>
  const idPart =
    (r?.fields as any)?.link?._id?.value ??
    (r?.fields as any)?.document?._id?.value ??
    (r?.fields as any)?._id?.value ??
    rowIndex
  return `tile-${rowIndex}-${String(idPart)}`
}

/** Иммутабельно патчит одно поле в объекте строки UniDataTable. */
function swingPatchRowField(row: unknown, fieldName: string, raw: unknown): Record<string, unknown> {
  const base =
    row && typeof row === 'object' && !Array.isArray(row)
      ? { ...(row as Record<string, unknown>) }
      : { fields: {} }
  const fields = {
    ...(((base.fields as Record<string, unknown>) || {}) as Record<string, unknown>),
  }
  fields[fieldName] = setType(raw)
  return { ...base, fields }
}

/** Запись поля строки в modelValue (v-model tiles). */
function swingCommitRowField(rowIndex: number, fieldName: string, raw: unknown): void {
  const nextRows = normalizeTilesRows(props.modelValue).slice()
  while (nextRows.length <= rowIndex) nextRows.push({ fields: {} })
  nextRows[rowIndex] = swingPatchRowField(nextRows[rowIndex], fieldName, raw)
  emit('update:modelValue', { rows: nextRows })
}

/** Чтение значения колонки строки (для fallback HTML и скриптов клона). */
function swingGetRowFieldValue(rowIndex: number, fieldName: string): unknown {
  const rows = rowsResolved.value
  const row = rows[rowIndex]
  return extractUniDataRowColumnValue(row, fieldName)
}

/**
 * Выполняет `<inlineAfterLoad>` для одной строки после mount клона.
 * web_client_exec=true → En на клиенте; иначе middle executeScript (WUI-408 / WUI-421).
 */
async function swingInlineAfterLoad(rowIndex: number): Promise<void> {
  const script = resolveTilesInlineAfterLoadScript(props.inlineAfterLoad)
  if (!script.text) return

  // Race: signature watch может сработать до появления panel template.
  if (!panelTemplate.value) {
    await nextTick()
    if (!panelTemplate.value) {
      console.warn('[MTiles] inlineAfterLoad skipped: panel template not ready', {
        rowIndex,
        tilePanelId: props.tilePanelId,
      })
      return
    }
  }

  // Child afterRowMount runs before parent ElementRenderer registerElement(Tiles).
  // Wait briefly, then inject a temporary setInlineVisibility hook if still missing.
  const elementsMap = () =>
    formProvider?.getElements?.() as Record<string, Record<string, unknown>> | undefined
  const tilesElementId = Number(props.tilesElementId)
  if (Number.isFinite(tilesElementId) && tilesElementId > 0) {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      if (isTilesElementWithInlineVisibilityRegistered(elementsMap(), tilesElementId)) break
      await nextTick()
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
    }
  }

  const cleanupTempHook = ensureTilesInlineVisibilityForInlineAfterLoad(
    elementsMap(),
    props.tilesElementId,
    swingSetInlineVisibility,
  )

  const selectedData = buildTilesInlineRowSelectedData(rowsResolved.value[rowIndex])
  const formId = pageRegistryFormId.value || props.formId
  const slotId = pageDataSlot.value || String(props.formId || '')
  const tilesRowScope = {
    tableFieldName: String(props.tableFieldName || ''),
    rowIndex,
    tilesElementId: props.tilesElementId,
    parentPanelId: props.parentPanelId,
  }
  const onClient = shouldRunTilesInlineAfterLoadOnClient(script)

  swingCurrentInlineRowIndex.value = rowIndex
  try {
    // WUI-408: web_client_exec=true → клиент; иначе middle executeScript (без preflight — WUI-421).
    if (onClient) {
      if (!formProvider?.runCode) return
      await formProvider.runCode(script.text, selectedData, {
        formId,
        pageDataSlot: slotId,
        panelId: props.parentPanelId,
        tilesRowScope,
        preloadPageFields: true,
      })
      return
    }

    await swingInlineAfterLoadOnMiddle(rowIndex, script.text, selectedData.fields)
  } finally {
    cleanupTempHook?.()
    swingCurrentInlineRowIndex.value = null
    reapplyRowActionVisibilityToRegistry(rowIndex)
    requestAnimationFrame(() => reapplyRowActionVisibilityToRegistry(rowIndex))
  }
}

/**
 * Middle-путь inlineAfterLoad: POST executeScript с currentData строки,
 * затем remap states и setVisible со scope текущей панели.
 */
async function swingInlineAfterLoadOnMiddle(
  rowIndex: number,
  scriptBody: string,
  rowFields: Record<string, unknown>,
): Promise<void> {
  const pd = pageData.value as Record<string, any> | undefined
  const sessionId = pd?.sessionId
  const elementIdNum = Number(props.tilesElementId)
  if (!sessionId || !Number.isFinite(elementIdNum) || elementIdNum <= 0) {
    console.warn('[MTiles] inlineAfterLoad middle skipped: missing sessionId or tilesElementId', {
      sessionId,
      tilesElementId: props.tilesElementId,
    })
    return
  }

  const fields = buildTilesInlineAfterLoadCurrentDataFields(
    rowFields,
    String(props.tableFieldName || ''),
    rowIndex,
  )
  const currentData = createSafeClientData({
    fields: { fields },
  })

  try {
    const scriptResponse = await scriptService.executeScript({
      sessionId: String(sessionId),
      currentData,
      elementId: elementIdNum,
      level: ScriptLevel.ELEMENT,
      scriptName: ScriptName.INLINE_AFTER_LOAD,
      scriptBody,
    })
    const responseData = (scriptResponse as any)?.data || scriptResponse
    const states = remapExchangeStatesForTilesRow(
      normalizeExchangeStates(responseData),
      {
        tableFieldName: String(props.tableFieldName || ''),
        rowIndex,
      },
    )
    applyTilesRowVisibilityStates(states, formProvider?.setVisible, {
      formId: String(pageRegistryFormId.value || props.formId || ''),
      panelId: props.parentPanelId,
    })
  } catch (error) {
    console.error('[MTiles] inlineAfterLoad executeScript failed', {
      rowIndex,
      tilesElementId: props.tilesElementId,
      error,
    })
  }
}

/** Строки, видимые на экране (для SwingTiles.getData). */
function visibleRowsForGetData(): unknown[] {
  const all = rowsResolved.value
  return all.filter((row, rowIndex) => isRowVisible(row, rowIndex))
}

function readClonePanelFields(rowIndex: number): Record<string, unknown> {
  if (!formProvider?.getForm || !props.formId || !props.tilePanelId) return {}
  const slice = formProvider.getForm({
    formId: props.formId,
    panelId: props.tilePanelId,
    flatten: true,
    tilesRowScope: {
      tableFieldName: String(props.tableFieldName || ''),
      rowIndex,
      tilesElementId: props.tilesElementId,
      parentPanelId: props.parentPanelId,
    },
  })
  if (!slice || typeof slice !== 'object' || Array.isArray(slice)) return {}
  return slice as Record<string, unknown>
}

/** SwingTiles.getData: INLINE → getInlineData; иначе таблица видимых строк. */
function swingGetData(action?: unknown): UniData {
  if (isTilesInlineLayoutAction(action)) {
    const rowIndex = swingSelectedRowIndex.value
    if (rowIndex == null || rowIndex < 0) {
      return new UniData()
    }
    return new UniData(
      buildTilesInlineGetDataFields({
        tableFieldName: props.tableFieldName || '',
        rowIndex,
        rowData: rowsResolved.value[rowIndex],
        clonePanelFields: readClonePanelFields(rowIndex),
      }),
    )
  }
  const rows = visibleRowsForGetData()
  const ud = new UniData()
  ud.setField(props.tableFieldName || 'tiles', { rows })
  return ud
}

function swingSetInlineVisibility(a: boolean | string, b?: boolean): void {
  const ri = resolveTilesInlineAfterLoadRowIndex(
    swingCurrentInlineRowIndex.value,
    formProvider?.getTilesRowScope?.() ?? null,
    {
      tableFieldName: props.tableFieldName,
      tilesElementId: props.tilesElementId,
    },
  )
  if (ri === null) {
    console.warn(
      '[MTiles] setInlineVisibility ignored: no active inlineAfterLoad row (call outside per-row script)',
    )
    return
  }
  if (typeof a === 'boolean' && b === undefined) {
    rowWrapVisible.value = { ...rowWrapVisible.value, [ri]: a }
    return
  }
  if (typeof a === 'string' && typeof b === 'boolean') {
    rowActionVisibility.value = setTilesRowActionVisibility(rowActionVisibility.value, ri, a, b)
    const fn = buildTilesInlineElementVisibilityName(props.tableFieldName || '', ri, a)
    const formId = String(pageRegistryFormId.value || props.formId || '')
    const panelId = String(props.parentPanelId || '')
    formProvider?.setVisible?.(fn, b, {
      formId: formId || undefined,
      panelId: panelId || undefined,
    })
  }
}

/** Повторно применить row map к registry (MAction мог зарегистрироваться после inlineAfterLoad). Не пишет jsResult.states — scoped persist пропускается. */
function reapplyRowActionVisibilityToRegistry(rowIndex: number): void {
  const rowMap = rowActionVisibility.value[rowIndex]
  if (!rowMap || !formProvider?.setVisible) return
  const formId = String(pageRegistryFormId.value || props.formId || '')
  const panelId = String(props.parentPanelId || '')
  const scope = formId || panelId ? { formId: formId || undefined, panelId: panelId || undefined } : undefined
  for (const [actionName, visible] of Object.entries(rowMap)) {
    const fn = buildTilesInlineElementVisibilityName(props.tableFieldName || '', rowIndex, actionName)
    formProvider.setVisible(fn, visible, scope)
  }
}

defineExpose({
  swingGetData,
  swingSetInlineVisibility,
})

function requestLazyPageReloadIfNeeded(): void {
  const pid = resolveLazyPageId()
  const fid = String(props.formId || pageRegistryFormId.value || '')
  if (!formProvider?.reloadLazyPage || !pid || !fid || lazyReloadRequested.value) return
  lazyReloadRequested.value = true
  void formProvider
    .reloadLazyPage(pid, fid)
    .catch((error: unknown) => {
      console.error('[MTiles] reloadLazyPage failed', { pageId: pid, formId: fid, error })
    })
    .finally(() => {
      lazyReloadRequested.value = false
    })
}

watch(
  () => [rowsResolved.value.length, props.pageId, props.formId, props.parentPanelId] as const,
  ([rowsCount]) => {
    if (rowsCount > 0) return
    requestLazyPageReloadIfNeeded()
  },
  { immediate: true },
)

/** Строки уже есть, но panel clone ещё не в form.panels — подгрузить lazy page. */
watch(
  () => [rowsResolved.value.length, panelTemplate.value, props.tilePanelId] as const,
  ([rowsCount, template]) => {
    if (rowsCount <= 0 || template || !props.tilePanelId) return
    requestLazyPageReloadIfNeeded()
  },
  { immediate: true },
)

watch(
  rowActionVisibility,
  () => {
    for (let rowIndex = 0; rowIndex < rowsResolved.value.length; rowIndex += 1) {
      reapplyRowActionVisibilityToRegistry(rowIndex)
    }
  },
  { deep: true, flush: 'post' },
)

/** После смены данных строк (remove service, reloadPanel) — повторить inlineAfterLoad per-row. */
const inlineAfterLoadRowsSignature = computed(() => {
  const script = resolveTilesInlineAfterLoadScript(props.inlineAfterLoad)
  return buildTilesInlineAfterLoadRowsSignature({
    rows: rowsResolved.value,
    scriptText: script.text,
    readRowField: (row, fieldName) => extractUniDataRowColumnValue(row, fieldName),
  })
})
/** Per-row signature segments — re-run только изменившиеся строки. */
let inlineAfterLoadRowSignatures: Record<number, string> = {}
let inlineAfterLoadSignatureSeeded = false
/** Indexes waiting to run; signature for these is NOT committed until queued. */
let pendingInlineAfterLoadIndexes: number[] = []

function commitAndQueueInlineAfterLoadRows(rowIndexes: number[], scriptText: string): void {
  const readRowField = (row: unknown, fieldName: string) =>
    extractUniDataRowColumnValue(row, fieldName)
  const rows = rowsResolved.value
  const nextSigs = { ...inlineAfterLoadRowSignatures }
  for (const rowIndex of rowIndexes) {
    if (rowIndex < 0 || rowIndex >= rows.length) continue
    nextSigs[rowIndex] = buildTilesInlineAfterLoadRowSignature(
      rows[rowIndex],
      rowIndex,
      scriptText,
      readRowField,
    )
    queueInlineAfterLoad(rowIndex)
  }
  for (const key of Object.keys(nextSigs)) {
    if (Number(key) >= rows.length) {
      delete nextSigs[Number(key)]
    }
  }
  inlineAfterLoadRowSignatures = nextSigs
}

async function flushPendingInlineAfterLoad(): Promise<void> {
  if (pendingInlineAfterLoadIndexes.length === 0) return
  await nextTick()
  if (!panelTemplate.value) return
  const script = resolveTilesInlineAfterLoadScript(props.inlineAfterLoad)
  if (!script.text) {
    pendingInlineAfterLoadIndexes = []
    return
  }
  const toRun = [...new Set(pendingInlineAfterLoadIndexes)]
  pendingInlineAfterLoadIndexes = []
  commitAndQueueInlineAfterLoadRows(toRun, script.text)
}

watch(
  inlineAfterLoadRowsSignature,
  (signature) => {
    const script = resolveTilesInlineAfterLoadScript(props.inlineAfterLoad)
    if (!script.text || !signature) return

    const readRowField = (row: unknown, fieldName: string) =>
      extractUniDataRowColumnValue(row, fieldName)
    const rows = rowsResolved.value

    if (!inlineAfterLoadSignatureSeeded) {
      inlineAfterLoadSignatureSeeded = true
      pendingInlineAfterLoadIndexes = rows.map((_, i) => i)
      void flushPendingInlineAfterLoad()
      return
    }

    const { changedIndexes, nextByIndex } = collectChangedInlineAfterLoadRowIndexes(
      inlineAfterLoadRowSignatures,
      rows,
      script.text,
      readRowField,
    )

    // Commit only unchanged rows; changed stay pending until queue succeeds.
    const nextSigs: Record<number, string> = {}
    for (let i = 0; i < rows.length; i += 1) {
      if (!changedIndexes.includes(i)) {
        nextSigs[i] = nextByIndex[i] ?? inlineAfterLoadRowSignatures[i]
      } else if (inlineAfterLoadRowSignatures[i] != null) {
        nextSigs[i] = inlineAfterLoadRowSignatures[i]
      }
    }
    inlineAfterLoadRowSignatures = nextSigs

    if (changedIndexes.length === 0) return
    pendingInlineAfterLoadIndexes = [
      ...new Set([...pendingInlineAfterLoadIndexes, ...changedIndexes]),
    ]
    void flushPendingInlineAfterLoad()
  },
  { flush: 'post' },
)

/** Panel clone ready / remount — flush pending and initial row scripts. */
watch(
  () => panelTemplate.value,
  (template, prev) => {
    const script = resolveTilesInlineAfterLoadScript(props.inlineAfterLoad)
    if (!script.text || !template) return
    if (rowsResolved.value.length <= 0) return
    if (pendingInlineAfterLoadIndexes.length > 0) {
      void flushPendingInlineAfterLoad()
    }
    if (!prev) {
      for (let rowIndex = 0; rowIndex < rowsResolved.value.length; rowIndex += 1) {
        queueInlineAfterLoad(rowIndex)
      }
      void nextTick(() => {
        for (let rowIndex = 0; rowIndex < rowsResolved.value.length; rowIndex += 1) {
          reapplyRowActionVisibilityToRegistry(rowIndex)
        }
      })
    }
  },
  { flush: 'post' },
)

/**
 * reload/panel обновил table fields — пересчитать inlineAfterLoad для всех строк этой таблицы.
 * Sticky map не очищаем заранее (иначе мигание: rowReady=false на всех строках).
 */
watch(
  () => formProvider?.getTilesInlineAfterLoadEpoch?.()?.value ?? 0,
  (epoch, prevEpoch) => {
    if (!epoch || epoch === prevEpoch) return
    const script = resolveTilesInlineAfterLoadScript(props.inlineAfterLoad)
    if (!script.text || rowsResolved.value.length <= 0) return
    const myTable = String(props.tableFieldName || '').trim()
    const reloadTables = formProvider?.getTilesInlineAfterLoadReloadTables?.()?.value ?? []
    if (myTable && reloadTables.length > 0 && !reloadTables.includes(myTable)) {
      return
    }
    const toRun = listTilesRowIndexesForReloadInlineAfterLoad(rowsResolved.value.length)
    pendingInlineAfterLoadIndexes = [
      ...new Set([...pendingInlineAfterLoadIndexes, ...toRun]),
    ]
    void flushPendingInlineAfterLoad()
  },
  { flush: 'post' },
)

</script>

<style scoped lang="scss">
.m-tiles-wrapper {
  width: 100%;
}

.m-tiles-items {
  width: 100%;
}

.m-tiles-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>
